import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Eye, Truck, ShieldCheck, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, eur } from "../lib/api";
import { useCart } from "../context/CartContext";

const CATEGORIES = [
  { key: null, label: "Todo" },
  { key: "experiencia", label: "Experiencias" },
  { key: "servicio", label: "Servicios" },
  { key: "equipamiento", label: "Equipamiento" },
];

const TRUST = [
  { icon: Truck, title: "Gestión rápida", text: "Confirmamos tu pedido en 24/48h" },
  { icon: ShieldCheck, title: "Pago seguro", text: "Tarjeta vía Redsys o Bizum" },
  { icon: Phone, title: "Atención directa", text: "678 783 000 · 664 069 630" },
];

export default function Tienda() {
  const [products, setProducts] = useState(null);
  const [cat, setCat] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  }, []);

  const filtered = products ? (cat ? products.filter((p) => p.category === cat) : products) : null;

  return (
    <div data-testid="tienda-page">
      {/* ANNOUNCEMENT BAR */}
      <div className="bg-gold text-black text-center py-2.5 px-4" data-testid="shop-announcement">
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold">
          Pago seguro con Redsys y Bizum · Atención telefónica 678 783 000
        </p>
      </div>

      {/* SHOP HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1591121768868-f537609281ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxodW50aW5nJTIwZ2VhcnxlbnwwfHx8fDE3ODYwOTc5Mzh8MA&ixlib=rb-4.1.0&q=85" alt="Equipamiento de caza" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="overline-gold mb-4">Tienda del club</p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl text-white font-light tracking-tight hero-text-shadow uppercase">
              Experiencias y <span className="text-gold italic">Equipamiento</span>
            </h1>
            <p className="mt-5 text-white/75 font-light max-w-xl hero-text-shadow">
              Recechos, y merchandising oficial de Cinegética Ruta del Agua. Pago seguro con tarjeta (Redsys) o Bizum.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FILTERS */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12" data-testid="category-filters">
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button key={c.label} onClick={() => setCat(c.key)}
              className={`px-5 py-2 text-[11px] uppercase tracking-[0.15em] font-semibold border transition-colors duration-200 ${
                cat === c.key ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white hover:border-white/40"
              }`}
              data-testid={`filter-${c.label.toLowerCase()}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* PRODUCT GRID */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        {filtered === null ? (
          <div className="py-24 flex justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-24 text-white/40" data-testid="no-products">No hay productos disponibles.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {filtered.map((p, i) => (
              <motion.article key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="group" data-testid={`product-card-${p.id}`}>
                <div className="relative aspect-[4/5] overflow-hidden bg-surface border border-white/5">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-4 left-4 bg-black/70 backdrop-blur text-gold text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                    {p.category}
                  </span>
                  <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex">
                    <Link to={`/tienda/${p.id}`}
                      className="flex-1 bg-black/80 backdrop-blur text-white text-[11px] uppercase tracking-[0.15em] font-semibold py-4 flex items-center justify-center gap-2 hover:text-gold transition-colors duration-200"
                      data-testid={`product-view-${p.id}`}>
                      <Eye size={15} /> Vista Rápida
                    </Link>
                    {p.price > 0 ? (
                      <button onClick={() => { add(p); toast.success("Añadido al carrito"); }}
                        className="flex-1 bg-gold text-black text-[11px] uppercase tracking-[0.15em] font-bold py-4 flex items-center justify-center gap-2 hover:bg-sand transition-colors duration-200"
                        data-testid={`product-add-${p.id}`}>
                        <ShoppingBag size={15} /> Añadir
                      </button>
                    ) : (
                      <a href={`https://wa.me/34678783000?text=${encodeURIComponent(`Hola, me interesa: ${p.name}. ¿Precio y disponibilidad?`)}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 bg-gold text-black text-[11px] uppercase tracking-[0.15em] font-bold py-4 flex items-center justify-center gap-2 hover:bg-sand transition-colors duration-200"
                        data-testid={`product-consult-${p.id}`}>
                        Consultar
                      </a>
                    )}
                  </div>
                </div>
                <div className="pt-4 flex items-start justify-between gap-4">
                  <div>
                    <Link to={`/tienda/${p.id}`}>
                      <h3 className="text-lg text-white font-medium uppercase tracking-wide hover:text-gold transition-colors duration-200">{p.name}</h3>
                    </Link>
                    <p className="mt-1 text-white/45 text-xs font-light line-clamp-1">{p.description}</p>
                  </div>
                  <p className="font-heading text-2xl text-gold whitespace-nowrap" data-testid={`product-price-${p.id}`}>
                    {p.price > 0 ? eur(p.price) : "A consultar"}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {/* TRUST STRIP */}
      <section className="border-t border-white/5 bg-[#0D0D0D]" data-testid="shop-trust">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
          {TRUST.map((t) => (
            <div key={t.title} className="flex items-start gap-4">
              <t.icon size={24} strokeWidth={1.25} className="text-gold shrink-0 mt-1" />
              <div>
                <p className="text-white font-medium uppercase tracking-wide text-sm">{t.title}</p>
                <p className="text-white/50 text-sm font-light mt-1">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
